/**
 * Asset Manager & Texture Atlas Loader
 * Implements Phaser-Core & PixiJS Asset Loader & Animation patterns.
 * Preloads sprite sheets, parses frame metadata, and renders fluid animated frames with blend modes.
 */

export interface AnimationMeta {
  start: number;
  end: number;
  count: number;
  frameRate: number;
}

export interface AtlasMeta {
  frameWidth: number;
  frameHeight: number;
  animations: Record<string, AnimationMeta>;
}

class AssetManagerClass {
  private sheets: Map<string, HTMLImageElement> = new Map();
  private metadata: Map<string, AtlasMeta> = new Map();
  private isLoaded: boolean = false;

  public async preloadAll(): Promise<void> {
    if (this.isLoaded || typeof window === "undefined") return;

    try {
      await Promise.all([
        this.loadAtlas(
          "player",
          "/games/bertahan-dari-anomali/assets/game/player/player_sheet.png",
          "/games/bertahan-dari-anomali/assets/game/player/player_sheet.json"
        ),
        this.loadAtlas(
          "enemy",
          "/games/bertahan-dari-anomali/assets/game/enemies/enemy_sheet.png",
          "/games/bertahan-dari-anomali/assets/game/enemies/enemy_sheet.json"
        ),
        this.loadAtlas(
          "vfx",
          "/games/bertahan-dari-anomali/assets/game/vfx/vfx_sheet.png",
          "/games/bertahan-dari-anomali/assets/game/vfx/vfx_sheet.json"
        ),
      ]);
      this.isLoaded = true;
      console.log("[AssetManager] All atlases successfully loaded!");
    } catch (err) {
      console.error("[AssetManager] Error loading atlases:", err);
    }
  }

  private async loadAtlas(key: string, imageSrc: string, jsonSrc: string): Promise<void> {
    try {
      const [img, json] = await Promise.all([
        this.loadImage(imageSrc),
        this.loadJson(jsonSrc),
      ]);
      this.sheets.set(key, img);
      this.metadata.set(key, json);
      console.log(`[AssetManager] Loaded atlas: ${key}`);
    } catch (err) {
      console.error(`[AssetManager] Failed to load atlas ${key}:`, err);
      throw err;
    }
  }

  private loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  private async loadJson(src: string): Promise<AtlasMeta> {
    const res = await fetch(src);
    return await res.json();
  }

  public hasAtlas(key: string): boolean {
    return this.sheets.has(key) && this.metadata.has(key);
  }

  /**
   * Draw an animated character frame from the loaded sprite atlas
   */
  public drawFrame(
    ctx: CanvasRenderingContext2D,
    key: string,
    animName: string,
    frameIndex: number,
    destX: number,
    destY: number,
    flipX: boolean = false,
    alpha: number = 1.0,
    filter?: string,
    scale: number = 1.0
  ): boolean {
    const img = this.sheets.get(key);
    const meta = this.metadata.get(key);
    if (!img || !meta || !meta.animations[animName]) return false;

    const anim = meta.animations[animName];
    const clampedFrame = anim.start + (Math.abs(frameIndex) % anim.count);

    const fw = meta.frameWidth;
    const fh = meta.frameHeight;
    const sx = clampedFrame * fw;
    const sy = 0;

    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
    if (filter) {
      ctx.filter = filter;
    }
    ctx.translate(destX, destY);

    if (flipX) {
      ctx.scale(-scale, scale);
    } else if (scale !== 1.0) {
      ctx.scale(scale, scale);
    }

    // Centered around destination with bottom foot alignment
    ctx.drawImage(img, sx, sy, fw, fh, -fw / 2, -fh + 12, fw, fh);
    ctx.restore();
    return true;
  }

  /**
   * Draw VFX frame with rotation, scaling, and Pixi-style additive blend mode
   */
  public drawVfxFrame(
    ctx: CanvasRenderingContext2D,
    animName: string,
    progress: number, // 0.0 to 1.0
    destX: number,
    destY: number,
    angleRad: number = 0,
    scale: number = 1.0,
    alpha: number = 1.0,
    additive: boolean = true
  ): boolean {
    const img = this.sheets.get("vfx");
    const meta = this.metadata.get("vfx");
    if (!img || !meta || !meta.animations[animName]) return false;

    const anim = meta.animations[animName];
    const frameOffset = Math.min(anim.count - 1, Math.max(0, Math.floor(progress * anim.count)));
    const frameIndex = anim.start + frameOffset;

    const fw = meta.frameWidth;
    const fh = meta.frameHeight;
    const sx = frameIndex * fw;
    const sy = 0;

    ctx.save();
    ctx.globalAlpha = Math.max(0, Math.min(1, alpha));
    if (additive) {
      ctx.globalCompositeOperation = "lighter";
    }
    ctx.translate(destX, destY);
    if (angleRad !== 0) {
      ctx.rotate(angleRad);
    }
    if (scale !== 1.0) {
      ctx.scale(scale, scale);
    }

    // Center-aligned
    ctx.drawImage(img, sx, sy, fw, fh, -fw / 2, -fh / 2, fw, fh);
    ctx.restore();
    return true;
  }
}

export const assetManager = new AssetManagerClass();
